import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

import { fetchUnitsList } from '../optionsSlice';

// Async thunk to fetch units data from API
export const fetchUnits = createAsyncThunk(
  'units/fetchUnits',
  async ({ page = 1, searchTerm="" }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/units-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch units');
    }
  }
);

// Async thunk to delete a unit
export const deleteUnit = createAsyncThunk(
  'units/deleteUnit',
  async (unitId, { dispatch, rejectWithValue }) => {
    try {
      await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/units-delete`, { id: unitId });
      dispatch(fetchUnits({ page: 1 }));
      dispatch(fetchUnitsList());
      return unitId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete unit');
    }
  }
);

// Async thunk to create a new unit
export const createUnit = createAsyncThunk(
  'units/createUnit',
  async (unitData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/units`, unitData);
      dispatch(fetchUnits({ page: unitData.page || 1 }));
      dispatch(fetchUnitsList());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create unit');
    }
  }
);

// Async thunk to update an existing unit
export const updateUnit = createAsyncThunk(
  'units/updateUnit',
  async (unitData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/units/`+unitData.id, unitData);
      dispatch(fetchUnits({ page: unitData.page || 1 }));
      dispatch(fetchUnitsList());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update unit');
    }
  }
);

const unitsSlice = createSlice({
  name: 'admin_units',
  initialState: {
    units: [],
    currentUnitPage: 1,
    totalUnitPages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    setUnitPage: (state, action) => {
      state.currentUnitPage = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = action.payload.data.data;
        state.currentUnitPage = action.payload.data.current_page || 1;
        state.totalUnitPages = action.payload.data.last_page || 0;
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.successMessage = 'Unit deleted successfully';
      })
      .addCase(deleteUnit.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Unit created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createUnit.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.units.findIndex(unit => unit.id === action.payload.data.id);
          if (index !== -1) {
            state.units[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Unit updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateUnit.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      });
  },
});

export const { setUnitPage, clearMessages } = unitsSlice.actions;

export default unitsSlice.reducer;